import { CalendarDaysIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { cn } from "~/lib/utils";
import { useInspectionActivity, type InspectionActivitySnapshot } from "../../../state/governance";
import type { OrgProfileUnavailableReason } from "../../../state/orgProfile";
import { Button } from "../../ui/button";
import { GitHubActivity } from "../../ui/github-activity";
import { activityWeeks } from "../../ui/github-activity-calendar";
import { Bone } from "../LoadingSkeletons";

export function InspectionActivity({ snapshot, isPending = false, unavailableReason = null, onRetry, className, action, initialMonths = 6, layout = "standalone" }: {
  readonly snapshot: InspectionActivitySnapshot | null;
  readonly isPending?: boolean;
  readonly unavailableReason?: OrgProfileUnavailableReason | null;
  readonly onRetry?: () => void;
  readonly className?: string;
  readonly action?: ReactNode;
  readonly initialMonths?: 6 | 12;
  readonly layout?: "standalone" | "dashboard";
}) {
  const [months, setMonths] = useState<6 | 12>(initialMonths);
  const days = snapshot ? activityWeeks(snapshot.days, snapshot.to, months).flat().filter((day) => day !== null) : [];
  const total = days.reduce((sum, day) => sum + day.count, 0);
  return (
    <section data-slot="inspection-activity" aria-label="Inspection activity" aria-busy={isPending && snapshot === null}
      className={cn("flex w-full min-w-0 flex-col rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]", layout === "dashboard" ? "h-full" : "self-start", className)}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-foreground"><CalendarDaysIcon aria-hidden className="size-4 text-muted-foreground" />Inspection activity</h2>
        <div className="ml-auto flex items-center gap-3">
          {action ? <span className="text-xs">{action}</span> : null}
          <div role="group" aria-label="Activity period" className="flex gap-0.5 rounded-lg bg-muted/60 p-0.5">
            {([6, 12] as const).map((value) => <button key={value} type="button" aria-pressed={months === value}
              onClick={() => setMonths(value)} className={cn("rounded-md px-2.5 py-1 text-[11px] font-medium outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring", months === value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>{value === 6 ? "6m" : "1y"}</button>)}
          </div>
        </div>
      </header>
      {unavailableReason ? (
        <div role="status" className="my-auto py-10 text-sm text-muted-foreground">
          <p>Inspection history could not be loaded.</p>
          {onRetry ? <Button size="sm" variant="outline" className="mt-3" onClick={onRetry}>Try again</Button> : null}
        </div>
      ) : snapshot === null && !isPending ? (
        <p role="status" className="my-auto py-10 text-sm text-muted-foreground">Select an organization to see inspection activity.</p>
      ) : snapshot === null ? (
        <div role="status" className="flex flex-1 flex-col justify-center gap-4 py-6">
          <span className="sr-only">Loading inspection activity</span>
          <Bone className="h-32 w-full rounded-lg" />
        </div>
      ) : (
        <>
          <div className={cn("pt-3", layout === "dashboard" && "flex min-h-0 flex-1 items-center")}><GitHubActivity days={snapshot.days} to={snapshot.to} months={months} {...(layout === "dashboard" ? { maxCellSize: 24 } : {})} /></div>
          {total === 0 ? <p className="mt-3 text-xs text-muted-foreground">No checks recorded in this period.</p> : null}
        </>
      )}
    </section>
  );
}

export function InspectionActivityCard({ organizationId, ...props }: {
  readonly organizationId: string | null;
  readonly className?: string;
  readonly action?: ReactNode;
  readonly initialMonths?: 6 | 12;
  readonly layout?: "standalone" | "dashboard";
}) {
  const view = useInspectionActivity(organizationId);
  return <InspectionActivity {...props} snapshot={view.snapshot} isPending={view.isPending}
    unavailableReason={view.unavailableReason} onRetry={view.refresh} />;
}
